import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Redux } from 'src/store'

interface PublicHolidayField {
  id: string
  type: string
}

enum ImportMode {
  JSON_URL = 'json_url',
  JSON_PAYLOAD = 'json_payload'
}

export { ImportMode }

interface ImportPublicHolidaysPayload {
  mode: ImportMode
  storeId?: number
  jsonUrl?: string
  fields?: PublicHolidayField[]
  records?: (string | number)[][]
}

interface ImportPublicHolidaysResponse {
  insertedCount: number
  inserted: any[]
}

interface ImportState {
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  errorMessage: string | null
  result: ImportPublicHolidaysResponse | null
}

const initialState: ImportState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null,
  result: null
}

export const importPublicHolidays = createAsyncThunk<ImportPublicHolidaysResponse, ImportPublicHolidaysPayload, Redux>(
  'storeSurchargeImport/importPublicHolidays',
  async (payload, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('/store-surcharges/import-public-holidays', payload)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue({ message: 'An unexpected error occurred' })
    }
  }
)

export const storeSurchargeImportSlice = createSlice({
  name: 'storeSurchargeImport',
  initialState,
  reducers: {
    resetImportState: state => {
      state.isLoading = false
      state.isSuccess = false
      state.isError = false
      state.errorMessage = null
      state.result = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(importPublicHolidays.pending, state => {
        state.isLoading = true
        state.isSuccess = false
        state.isError = false
        state.errorMessage = null
        state.result = null
      })
      .addCase(importPublicHolidays.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.isError = false
        state.errorMessage = null
        state.result = action.payload
      })
      .addCase(importPublicHolidays.rejected, (state, action) => {
        state.isLoading = false
        state.isSuccess = false
        state.isError = true
        state.errorMessage = (action.payload as any)?.message || 'Import failed'
        state.result = null
      })
  }
})

export const { resetImportState } = storeSurchargeImportSlice.actions
export default storeSurchargeImportSlice.reducer
